<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  ArrowRight,
  MonitorCheck,
  RefreshCw,
  TriangleAlert,
} from 'lucide-vue-next'
import { useLiveStore } from '@/stores/live'

const MINIMUM_VISIBLE_MS = 650
const startupLogoUrl = `${import.meta.env.BASE_URL}icon-192.png`

const { t } = useI18n()
const live = useLiveStore()
const visible = ref(live.ocrStatus !== 'ready')
const dismissed = ref(false)
let shownAt = Date.now()
let hideTimer: number | undefined

const shouldShow = computed(() => visible.value && !dismissed.value)
const stageLabel = computed(() => live.ocrStatus === 'error'
  ? t('startup.errorStatus')
  : t(`startup.${live.ocrStage}`))
const errorTitle = computed(() => t(`startup.${live.ocrErrorType ?? 'unknown'}ErrorTitle`))
const errorHint = computed(() => t(`startup.${live.ocrErrorType ?? 'unknown'}ErrorHint`))
const progressValue = computed(() => Math.min(100, Math.max(0, live.ocrProgress)))

function clearHideTimer(): void {
  if (hideTimer === undefined) return
  window.clearTimeout(hideTimer)
  hideTimer = undefined
}

function show(): void {
  clearHideTimer()
  if (!visible.value) {
    shownAt = Date.now()
    visible.value = true
  }
}

function hideWhenStable(): void {
  clearHideTimer()
  const delay = Math.max(0, MINIMUM_VISIBLE_MS - (Date.now() - shownAt))
  hideTimer = window.setTimeout(() => {
    hideTimer = undefined
    visible.value = false
  }, delay)
}

function retry(): void {
  dismissed.value = false
  shownAt = Date.now()
  visible.value = true
  void live.initializeOcr()
}

watch(() => live.ocrStatus, (status) => {
  if (status === 'ready') hideWhenStable()
  else if (!dismissed.value) show()
}, { immediate: true })

watch(shouldShow, (showing) => {
  document.body.classList.toggle('ocr-startup-open', showing)
}, { immediate: true })

onBeforeUnmount(() => {
  clearHideTimer()
  document.body.classList.remove('ocr-startup-open')
})
</script>

<template>
  <Transition name="resource-gate">
    <div
      v-if="shouldShow"
      class="resource-gate"
      role="dialog"
      aria-modal="true"
      :aria-label="t('startup.title')"
    >
      <section
        class="startup-panel"
        :class="{ 'is-error': live.ocrStatus === 'error' }"
        :aria-labelledby="live.ocrStatus === 'error' ? 'ocr-error-title' : 'ocr-startup-title'"
        :aria-describedby="live.ocrStatus === 'error' ? 'ocr-error-description ocr-browser-recommendation' : 'ocr-startup-description'"
        :aria-busy="live.ocrStatus !== 'error' && live.ocrStatus !== 'ready'"
      >
        <div class="startup-content">
          <div class="logo-status" aria-hidden="true">
            <span class="logo-shell" :class="{ loading: live.ocrStatus !== 'error', error: live.ocrStatus === 'error' }">
              <TriangleAlert v-if="live.ocrStatus === 'error'" />
              <img v-else :src="startupLogoUrl" alt="" width="64" height="64">
            </span>
          </div>

          <div class="startup-copy">
            <span class="startup-brand">{{ t('app.name') }}</span>
            <h2 :id="live.ocrStatus === 'error' ? 'ocr-error-title' : 'ocr-startup-title'">
              {{ live.ocrStatus === 'error' ? errorTitle : t('startup.title') }}
            </h2>
            <p :id="live.ocrStatus === 'error' ? 'ocr-error-description' : 'ocr-startup-description'">
              {{ live.ocrStatus === 'error' ? errorHint : t('startup.subtitle') }}
            </p>

            <div
              v-if="live.ocrStatus === 'error'"
              id="ocr-browser-recommendation"
              class="browser-recommendation"
              role="note"
            >
              <span class="browser-recommendation-icon" aria-hidden="true"><MonitorCheck /></span>
              <span>
                <strong>{{ t('startup.browserRecommendationTitle') }}</strong>
                <small>{{ t('startup.browserRecommendationHint') }}</small>
              </span>
            </div>

            <div class="startup-progress">
              <div class="progress-status" aria-live="polite" aria-atomic="true">
                <span class="stage-label">
                  <TriangleAlert v-if="live.ocrStatus === 'error'" aria-hidden="true" />
                  <span v-else class="status-dot" aria-hidden="true" />
                  <Transition name="stage-swap" mode="out-in">
                    <strong :key="`${live.ocrStatus}:${live.ocrStage}`">{{ stageLabel }}</strong>
                  </Transition>
                </span>
                <output>{{ progressValue }}%</output>
              </div>
              <div
                class="progress-track"
                role="progressbar"
                :aria-label="t('startup.progress')"
                aria-valuemin="0"
                aria-valuemax="100"
                :aria-valuenow="progressValue"
              >
                <span class="progress-value" :style="{ transform: `scaleX(${progressValue / 100})` }" />
              </div>
              <small v-if="live.ocrStatus !== 'error'">{{ t('startup.localHint') }}</small>
            </div>

          <div v-if="live.ocrStatus === 'error'" class="startup-actions">
            <el-button type="primary" @click="retry"><RefreshCw aria-hidden="true" />{{ t('startup.retry') }}</el-button>
            <el-button @click="dismissed = true">{{ t('startup.continue') }}<ArrowRight aria-hidden="true" /></el-button>
          </div>
          </div>
        </div>
      </section>
    </div>
  </Transition>
</template>

<style scoped>
:global(body.ocr-startup-open) { overflow: hidden; }
.resource-gate {
  position: fixed;
  inset: 0;
  z-index: 900;
  display: grid;
  place-items: center;
  overflow: auto;
  padding: 1rem;
  background: var(--bg);
}
.startup-panel {
  width: min(31rem, 100%);
  overflow: hidden;
  contain: layout paint;
  border: 0.0625rem solid var(--border);
  border-radius: 0.75rem;
  background: var(--surface-raised);
  box-shadow: var(--shadow-card);
}
.startup-content {
  padding: 2.25rem 2rem 1.75rem;
  display: grid;
  justify-items: center;
  gap: 1.375rem;
}
.logo-status {
  position: relative;
  width: 5.5rem;
  height: 5.5rem;
  display: grid;
  place-items: center;
}
.logo-shell {
  position: relative;
  z-index: 1;
  width: 5rem;
  height: 5rem;
  display: grid;
  place-items: center;
  border: 0.0625rem solid var(--border);
  border-radius: 50%;
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}
.logo-shell.loading::after {
  content: '';
  position: absolute;
  inset: -0.375rem;
  z-index: -1;
  border: 0.0625rem solid color-mix(in srgb, var(--primary) 38%, var(--border));
  border-radius: 50%;
  animation: logo-breathe 2.6s ease-in-out infinite;
}
.logo-shell img { width: 4rem; height: 4rem; object-fit: contain; image-rendering: auto; }
.logo-shell > svg { width: 1.75rem; height: 1.75rem; color: var(--danger); }
.logo-shell.error { border-color: color-mix(in srgb, var(--danger) 38%, var(--border)); background: color-mix(in srgb, var(--danger) 8%, var(--surface)); }
.startup-copy { width: 100%; min-width: 0; text-align: center; }
.startup-brand {
  display: block;
  color: var(--primary);
  font-size: var(--font-label);
  line-height: 1.4;
  font-weight: 750;
}
.startup-copy h2 {
  margin: 0.375rem 0 0;
  color: var(--text-strong);
  font-size: 1.375rem;
  line-height: 1.25;
  font-weight: 760;
}
.startup-copy > p {
  max-width: 24rem;
  min-height: 2.25rem;
  margin: 0.5rem auto 0;
  color: var(--muted);
  font-size: var(--font-body);
  line-height: 1.55;
  overflow-wrap: anywhere;
}
.browser-recommendation {
  max-width: 25rem;
  margin: 1rem auto 0;
  padding: 0.75rem 0.875rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.6875rem;
  border-radius: 0.5rem;
  background: var(--surface-subtle);
  text-align: left;
}
.browser-recommendation-icon {
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--blue);
  background: var(--blue-soft);
}
.browser-recommendation-icon > svg { width: 1rem; height: 1rem; }
.browser-recommendation strong, .browser-recommendation small { display: block; }
.browser-recommendation strong { color: var(--text-strong); font-size: var(--font-label); font-weight: 680; }
.browser-recommendation small { margin-top: 0.1875rem; color: var(--muted); font-size: var(--font-caption); line-height: 1.5; }
.startup-progress { max-width: 25rem; margin: 1.375rem auto 0; text-align: left; }
.progress-status {
  min-height: 1.375rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.stage-label { min-width: 0; display: inline-flex; align-items: center; gap: 0.5rem; color: var(--text); font-size: var(--font-label); }
.stage-label > svg { width: 1rem; height: 1rem; flex: none; color: var(--danger); }
.status-dot { width: 0.5rem; height: 0.5rem; flex: none; border-radius: 50%; background: var(--green); animation: status-breathe 2.2s ease-in-out infinite; }
.stage-label strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.progress-status output {
  min-width: 2.75rem;
  color: var(--text-strong);
  font-size: var(--font-label);
  line-height: 1;
  font-weight: 720;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.progress-track {
  height: 0.375rem;
  overflow: hidden;
  border-radius: 62.4375rem;
  background: var(--surface-subtle);
}
.progress-value {
  display: block;
  width: 100%;
  height: 100%;
  transform-origin: left center;
  border-radius: inherit;
  background: var(--green);
  transition: transform 220ms var(--ease-out);
}
.is-error .progress-value { background: var(--danger); }
.startup-progress small {
  min-height: 1rem;
  margin-top: 0.625rem;
  display: block;
  color: var(--muted);
  font-size: var(--font-caption);
  line-height: 1.45;
}
.startup-actions { margin-top: 1.25rem; display: flex; justify-content: center; gap: 0.5rem; }
.startup-actions :deep(.el-button svg) { width: 1rem; height: 1rem; }
.resource-gate-enter-active { transition: opacity 180ms ease; }
.resource-gate-leave-active { transition: opacity 130ms ease; }
.resource-gate-enter-active .startup-panel { transition: opacity 180ms ease, transform 200ms var(--ease-out); }
.resource-gate-leave-active .startup-panel { transition: opacity 100ms ease, transform 130ms ease; }
.resource-gate-enter-from, .resource-gate-leave-to { opacity: 0; }
.resource-gate-enter-from .startup-panel { opacity: 0; transform: translateY(0.5rem); }
.resource-gate-leave-to .startup-panel { opacity: 0; transform: translateY(-0.25rem); }
.stage-swap-enter-active { transition: opacity 150ms ease, transform 170ms var(--ease-out); }
.stage-swap-leave-active { transition: opacity 90ms ease, transform 100ms ease; }
.stage-swap-enter-from { opacity: 0; transform: translateY(0.1875rem); }
.stage-swap-leave-to { opacity: 0; transform: translateY(-0.125rem); }

@keyframes logo-breathe {
  0%, 100% { opacity: 0.35; transform: scale(0.98); }
  50% { opacity: 0.8; transform: scale(1.035); }
}
@keyframes status-breathe {
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
}

@media (max-width: 42rem) {
  .resource-gate { padding: 0.75rem; }
  .startup-panel { margin-block: auto; }
  .startup-content { padding: 1.75rem 1.125rem 1.375rem; gap: 1.125rem; }
  .startup-copy h2 { font-size: 1.25rem; }
  .startup-copy > p { min-height: 0; }
  .startup-actions { display: grid; grid-template-columns: 1fr; }
}

@media (max-width: 25rem) {
  .startup-content { padding-inline: 1rem; }
}

@media (prefers-reduced-motion: reduce) {
  .logo-shell.loading::after, .status-dot { animation: none; }
  .resource-gate-enter-active, .resource-gate-leave-active,
  .resource-gate-enter-active .startup-panel, .resource-gate-leave-active .startup-panel,
  .stage-swap-enter-active, .stage-swap-leave-active,
  .progress-value { transition: none; }
}
</style>
